import React, { createRef, PureComponent, useEffect, useState, } from 'react';
import { Transition, FocusTrap } from '@headlessui/react';
import clsx from 'clsx';
import { BackgroundProvider } from '../BackgroundContext/index.js';
import { Divider } from '../Divider/index.js';
import { XIcon } from '../icons/index.js';
import { Container } from '../Layout/index.js';
import { Portal } from '../Portal/index.js';
import { setPropertyValue } from '../utils/css-utils.js';
import { FrameContext } from './FrameContext.js';
import { useMediaQuery } from './MediaQueryContext.js';
const MobileNavigationMarkup = ({ mobileNavShowing, handleNavigationDismiss, content, }) => {
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        setIsMounted(true);
    }, []);
    if (!isMounted) {
        return null;
    }
    return (React.createElement(Portal, null,
        React.createElement(Transition, { show: mobileNavShowing },
            React.createElement(Transition.Child, { enter: "transition-opacity duration-200", enterFrom: "opacity-0", enterTo: "opacity-100", leave: "transition-opacity duration-200", leaveFrom: "opacity-100", leaveTo: "opacity-0", className: "z-20 fixed inset-0 overflow-hidden" },
                React.createElement("div", { className: "absolute inset-0 bg-background-backdrop", onClick: handleNavigationDismiss, onTouchStart: handleNavigationDismiss })),
            React.createElement(Transition.Child, { enter: "ease-out duration-200 transform", enterFrom: "-translate-x-full", enterTo: "translate-x-0", leave: "ease-in duration-200 transform", leaveFrom: "translate-x-0", leaveTo: "-translate-x-full", className: "z-20 fixed inset-y-0 start-0 max-w-xs w-full bg-background px-3 py-4 overflow-y-auto", "aria-modal": "true", role: "dialog" },
                React.createElement(FocusTrap, null,
                    React.createElement(BackgroundProvider, { backgroundType: "main" },
                        React.createElement("button", { type: "button", onClick: handleNavigationDismiss, tabIndex: 0, className: "mb-5" },
                            React.createElement(XIcon, { className: "h-8 w-8", "aria-hidden": "true" })),
                        content))))));
};
const APP_FRAME_MAIN = 'FrameMain';
export class FrameInner extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            skipFocused: false,
            topBarStuck: true,
            topOffset: 0,
        };
        this.topBarRef = createRef();
        this.handleFocus = () => {
            this.setState({ skipFocused: true });
        };
        this.handleBlur = () => {
            this.setState({ skipFocused: false });
        };
        this.handleClick = (event) => {
            const { skipToContentTarget } = this.props;
            if (skipToContentTarget && skipToContentTarget.current) {
                skipToContentTarget.current.focus();
                event === null || event === void 0 ? void 0 : event.preventDefault();
            }
        };
        this.handleNavigationDismiss = () => {
            const { onNavigationDismiss } = this.props;
            if (onNavigationDismiss != null) {
                onNavigationDismiss();
            }
        };
        this.handleNavKeydown = (event) => {
            const { key } = event;
            const { mediaQuery: { isNavigationCollapsed: mobileSidebarEnabled }, mobileSidebarOpen, } = this.props;
            const mobileNavShowing = mobileSidebarEnabled && mobileSidebarOpen;
            if (mobileNavShowing && key === 'Escape') {
                this.handleNavigationDismiss();
            }
        };
        this.handleNavKeydown.bind(this);
    }
    componentDidMount() {
        if (typeof window !== 'undefined') {
            window.addEventListener('keydown', this.handleNavKeydown);
            this.styleObserver = new MutationObserver(mutations => {
                var _a, _b;
                const style = (_b = (_a = mutations === null || mutations === void 0 ? void 0 : mutations[0]) === null || _a === void 0 ? void 0 : _a.target) === null || _b === void 0 ? void 0 : _b.style;
                const currentValue = style === null || style === void 0 ? void 0 : style.getPropertyValue('--frame-top-offset');
                const { topOffset } = this.state;
                if (currentValue && currentValue !== topOffset) {
                    this.setState({
                        topOffset: parseInt(currentValue, 10),
                    });
                }
            });
            const { document } = this.props;
            this.styleObserver.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['style'],
            });
        }
        if (this.topBarRef.current) {
            this.headerSizeObserver = new ResizeObserver(() => {
                const { document } = this.props;
                setPropertyValue(document, '--c-top-bar-height', `${this.topBarRef.current.offsetHeight}px`);
            });
            this.headerSizeObserver.observe(this.topBarRef.current);
            this.intersectionObserver = new IntersectionObserver(([e]) => {
                this.setState({
                    topBarStuck: e.intersectionRatio < 1,
                });
            }, {
                threshold: [1],
            });
            this.intersectionObserver.observe(this.topBarRef.current);
        }
    }
    componentWillUnmount() {
        var _a, _b, _c, _d;
        (_a = this.styleObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
        if (typeof window !== 'undefined') {
            window.removeEventListener('keydown', this.handleNavKeydown);
        }
        if ((_b = this.topBarRef) === null || _b === void 0 ? void 0 : _b.current) {
            (_c = this.intersectionObserver) === null || _c === void 0 ? void 0 : _c.unobserve(this.topBarRef.current);
            (_d = this.headerSizeObserver) === null || _d === void 0 ? void 0 : _d.unobserve(this.topBarRef.current);
        }
    }
    render() {
        const { skipFocused, topOffset, topBarStuck } = this.state;
        const { logo, children, navigation, navigationSecondary, topBar, bottomBar, mobileSidebarOpen = false, skipToContentTarget, mediaQuery: { isNavigationCollapsed, bottomBarVisible }, fullWidth = false, layout = 'default', sticky = 'none', } = this.props;
        const mobileSidebarEnabled = isNavigationCollapsed;
        const mobileNavShowing = mobileSidebarEnabled && mobileSidebarOpen;
        const layoutContainsNavigation = !!navigation && ['no-header', 'default'].includes(layout);
        const layoutContainsMobileNavigation = layoutContainsNavigation && isNavigationCollapsed;
        const layoutContainsTopbar = !!topBar && ['no-sidebar', 'default'].includes(layout);
        const layoutContainsBottomBar = !!bottomBar &&
            bottomBarVisible &&
            (layout === 'default' || layout === 'no-sidebar');
        const bottomPadding = layoutContainsBottomBar &&
            'pb-[calc(var(--c-bottom-bar-height)+env(safe-area-inset-bottom)+1rem)]';
        const stickyTopBar = sticky === 'top-bar' || sticky === 'top-bar-navigation';
        const stickyNavigation = sticky === 'top-bar-navigation';
        const trackTopBar = stickyNavigation && topOffset > 0;
        const desktopNavigationMarkup = (React.createElement(Container, { size: "sm", direction: "vertical", spacing: "none", hide: { default: true, lg: false }, key: "NavContent", containerProps: {
                'aria-label': 'Navigation',
                className: clsx(stickyNavigation && [
                    'sticky',
                    layoutContainsTopbar && [
                        'top-[var(--c-top-bar-height)]',
                        !topBarStuck
                            ? 'max-h-[calc(100vh-var(--c-top-bar-height)-var(--frame-top-offset))]'
                            : 'max-h-[calc(100vh-var(--c-top-bar-height))]',
                    ],
                    'scrollbar-hide overscroll-contain',
                ], bottomPadding),
            } }, navigation));
        const navigationSecondaryMarkup = navigationSecondary ? (React.createElement("div", { className: "flex flex-col w-full space-y-5 px-3" },
            React.createElement(Divider, null),
            React.createElement("h4", null, "Navigation"),
            navigationSecondary)) : null;
        const topBarMarkup = layoutContainsTopbar ? (React.createElement(Container, { as: "header", size: "full", containerProps: {
                ref: this.topBarRef,
                role: 'banner',
                className: clsx('bg-topbar text-content-on-topbar', '-ms-[calc(env(safe-area-inset-left))] ps-[calc(env(safe-area-inset-left))]', '-me-[calc(env(safe-area-inset-right))] pe-[calc(env(safe-area-inset-right))]', stickyTopBar &&
                    'sticky z-20 inset-x-0 shadow border-b border-line-subdued', trackTopBar ? '-top-1' : 'top-0'),
            } }, topBar)) : null;
        const bottomBarMarkup = layoutContainsBottomBar ? (React.createElement("div", { className: clsx('fixed z-20 inset-x-0 bottom-0 shadow border-t border-line  bg-topbar text-content-on-topbar', 'h-[calc(var(--c-bottom-bar-height)+env(safe-area-inset-bottom))] pb-[calc(env(safe-area-inset-bottom))]') }, bottomBar)) : null;
        const skipTarget = (skipToContentTarget === null || skipToContentTarget === void 0 ? void 0 : skipToContentTarget.current)
            ? skipToContentTarget.current.id
            : APP_FRAME_MAIN;
        const skipMarkup = (React.createElement("a", { className: clsx('fixed top-4 start-4 z-30 p-1 bg-topbar text-content-on-topbar', skipFocused ? 'opacity-100' : 'opacity-0 pointer-events-none'), href: `#${skipTarget}`, onFocus: this.handleFocus, onBlur: this.handleBlur, onClick: this.handleClick }, "Skip to content"));
        const navigationAttributes = navigation
            ? {
                'data-has-navigation': true,
            }
            : {};
        const context = {
            logo,
            fullWidth,
        };
        return (React.createElement(FrameContext.Provider, { value: context },
            React.createElement(Container, { size: "full", spacing: "none", containerProps: {
                    className: clsx('bg-background min-h-screen', 'ms-[calc(env(safe-area-inset-left))]', 'me-[calc(env(safe-area-inset-right))]'),
                } },
                skipMarkup,
                topBarMarkup,
                React.createElement(Container, { size: "full", spacing: "none", padding: layoutContainsNavigation
                        ? { horizontal: { size: 'md', disableOnMobile: true } }
                        : null },
                    React.createElement(Container, { size: layoutContainsNavigation ? 'xl' : 'full', direction: "grid", containerProps: navigationAttributes },
                        layoutContainsMobileNavigation && (React.createElement(MobileNavigationMarkup, { mobileNavShowing: mobileNavShowing, handleNavigationDismiss: this.handleNavigationDismiss, content: React.createElement(React.Fragment, null,
                                navigation,
                                navigationSecondaryMarkup) })),
                        layoutContainsNavigation && desktopNavigationMarkup,
                        React.createElement(Container, { as: "main", size: layoutContainsNavigation ? 'lg' : 'full', spacing: "none", containerProps: {
                                id: 'FrameMain',
                                className: bottomPadding,
                            } }, children),
                        bottomBarMarkup)))));
    }
}
export function Frame(props) {
    const mediaQuery = useMediaQuery();
    return React.createElement(FrameInner, Object.assign({}, props, { mediaQuery: mediaQuery }));
}
//# sourceMappingURL=Frame.js.map